import { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { queryGetUserAssociatedBUList } from "@type/api/businessUnit";
import { useAuthStore } from "@type/store";
import { TBusinessUnit } from "@type/store/BusinessUnitStore";
import { useQueryWithCallbacks } from "lib/query/useQueryWithCallbacks";

interface UseGetUserAssociatedBUListParams {
  perPage?: number;
  page?: number;
}

export const useGetUserAssociatedBUList = ({
  perPage,
  page,
}: UseGetUserAssociatedBUListParams) => {
  const [loading, setLoading] = useState(true);
  const procesedPage = useRef<number[]>([]);
  const user = useAuthStore((state) => state.user);
  const [buList, setBuList] = useState<TBusinessUnit[]>([]);
  const [mapBUList, setMapBUList] = useState<Record<string, TBusinessUnit>>({});

  const [buListParams, setBUListParams] = useState({
    idMember: "",
    key: "",
    page: 0,
    pageSize: 100,
  });

  const queryGetUserAssociatedBUListKey = useMemo(() => {
    const { key, ...restParams } = buListParams;
    return queryGetUserAssociatedBUList._key(restParams, user?.language, key);
  }, [buListParams, user?.language]);

  useQueryWithCallbacks({
    queryKey: queryGetUserAssociatedBUListKey,
    enabled:
      !!buListParams?.page &&
      !!buListParams?.idMember &&
      !!buListParams?.page &&
      !!buListParams?.pageSize,
    queryFn: () => queryGetUserAssociatedBUList(buListParams, user?.language),
    onSuccess: (res) => {
      if (!procesedPage.current.includes(res.page)) {
        const newMapData: Record<string, TBusinessUnit> = {};
        const buListWithSlug: TBusinessUnit[] = [];
        for (const bu of res.data) {
          const buWithSlug = {
            ...bu,
            slug: bu.idUnique,
          };

          buListWithSlug.push(buWithSlug);
          newMapData[bu.idUnique] = buWithSlug;
        }

        if (res.page === 1) {
          setBuList(buListWithSlug);
          setMapBUList(newMapData);
        } else {
          const buListArray = Object.values(buListWithSlug);
          setBuList((prev) => [...prev, ...buListArray]);

          setMapBUList((prev) => ({
            ...prev,
            ...newMapData,
          }));
        }

        if (res.page * res.pageSize < res.total && !page) {
          setBUListParams((prev) => ({
            ...prev,
            key: uuidv4(),
            page: res.page + 1,
          }));
        } else {
          setLoading(false);
        }

        procesedPage.current = [...procesedPage.current, res.page];
      }
    },
  });

  useEffect(() => {
    if (user.idUnique && !buListParams.idMember) {
      setBUListParams((prev) => ({
        ...prev,
        key: uuidv4(),
        page: page || 1,
        pageSize: perPage || 100,
        idMember: user.idUnique,
      }));
    }
  }, [user.idUnique, buListParams?.idMember, perPage, page]);

  return {
    loading,
    mapBUList,
    buList,
  };
};
